import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import {
  fetchApiWithToken,
  hydraNextPageParam,
  type WithHydraPagination
} from '~/api/utils'
import type { ContactTypeFlags } from '~/domains/contacts/contactTypes'
import type { TaxonomyItems } from '~/domains/taxonomies/types'
import type { AddressResponse } from '~/types/global'

export type ContactResponse = {
  id: number
  firstname: string
  lastname: string
  email: string
  phone?: string
  address?: AddressResponse
  archived: boolean
  role?: {
    id: number
    name: string
  }
  customRole?: string
  company: {
    id: number
    name: string
  }
} & ContactTypeFlags

type CompanyResponse = {
  id: number
  name: string
  address?: AddressResponse
  contacts?: ContactResponse[]
  companyStatus?: {
    id: number
    name: string
    color: string
  }
  segmentations?: TaxonomyItems<'segmentations'>
}

export type CompaniesLDResponse = WithHydraPagination<CompanyResponse>

const fetchCompany = ({
  companyId,
  signal
}: {
  companyId: number
  signal?: AbortSignal
}) =>
  fetchApiWithToken<CompanyResponse>({
    url: `/v1/companies/${companyId}`,
    signal
  })

export const useCompany = (companyId?: number) => {
  return useQuery<CompanyResponse>({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) =>
      fetchCompany({ companyId: companyId ?? 0, signal }),
    enabled: !!companyId
  })
}

export const useCompanies = ({
  ids,
  search,
  enabled = true,
  showArchived = false,
  fields
}: {
  ids?: string[]
  search?: string
  enabled?: boolean
  showArchived?: boolean
  fields?: string[]
}) =>
  useInfiniteQuery<
    CompaniesLDResponse,
    Error,
    InfiniteData<CompaniesLDResponse>
  >({
    queryKey: ['companies', ids, showArchived, search, fields],
    initialPageParam: 1,
    placeholderData: (prev) => prev,
    getNextPageParam: hydraNextPageParam(),
    queryFn: async ({ pageParam, signal }) => {
      const queryParams = new URLSearchParams()
      if (ids) {
        for (const id of ids) {
          queryParams.append('id[]', id.toString())
        }
      }
      if (!showArchived) {
        queryParams.append('archived', 'false')
      }
      if (search) {
        queryParams.append('name', search)
      }
      queryParams.append('order[name]', 'asc')
      for (const field of fields ?? []) {
        queryParams.append('fields[]', field)
      }
      return (await fetchApiWithToken({
        method: 'get',
        url: `/v1/companies?${queryParams.toString()}&page=${pageParam}`,
        useLDJson: true,
        signal
      })) as CompaniesLDResponse
    },
    enabled,
    staleTime: 60_000
  })
